import type { ReactNode } from "react";

/** Houseful's line icons: 24-unit grid, 1.7 stroke, currentColor, always decorative (the control carries the name). */
function Icon({ size = 20, children }: { size?: number; children: ReactNode }) {
  return (
    <svg className="hf-ic" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

export const SearchIcon = ({ size = 20 }: { size?: number }) => (
  <Icon size={size}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.2-4.2" />
  </Icon>
);
export const UserIcon = () => (
  <Icon size={18}>
    <circle cx="12" cy="8.5" r="3.5" />
    <path d="M5 19.5c1.4-3 4-4.5 7-4.5s5.6 1.5 7 4.5" />
  </Icon>
);
export const MenuIcon = () => (
  <Icon size={22}>
    <path d="M4 8h16M4 16h16" />
  </Icon>
);
export const CloseIcon = () => (
  <Icon size={20}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Icon>
);
export const ArrowIcon = () => (
  <Icon size={18}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Icon>
);
export const PinIcon = () => (
  <Icon size={18}>
    <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" />
    <circle cx="12" cy="10" r="2.3" />
  </Icon>
);
export const ShareIcon = () => (
  <Icon size={18}>
    <path d="M12 4v11M7.5 8.5 12 4l4.5 4.5M5 13v5.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V13" />
  </Icon>
);
export const CheckIcon = () => (
  <Icon size={18}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </Icon>
);
export const GridIcon = () => (
  <Icon size={18}>
    <rect x="4" y="4" width="7" height="7" rx="1.5" />
    <rect x="13" y="4" width="7" height="7" rx="1.5" />
    <rect x="4" y="13" width="7" height="7" rx="1.5" />
    <rect x="13" y="13" width="7" height="7" rx="1.5" />
  </Icon>
);
export const PhoneIcon = () => (
  <Icon size={18}>
    <path d="M6.5 3.5h3l1.5 4-2 1.5a11 11 0 0 0 6 6l1.5-2 4 1.5v3A2 2 0 0 1 18.5 20 15 15 0 0 1 4 5.5a2 2 0 0 1 2.5-2z" />
  </Icon>
);
export const MailIcon = () => (
  <Icon size={18}>
    <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" />
    <path d="m4.5 7.5 7.5 5.5 7.5-5.5" />
  </Icon>
);

const FACT_PATHS: Record<"ruler" | "bath" | "sofa" | "calendar" | "bed", ReactNode> = {
  bed: <path d="M3.5 18V7.5M3.5 14h17v4M20.5 14v-2.5A2.5 2.5 0 0 0 18 9h-7v5M7 11.5a1.5 1.5 0 1 0 0-.01" />,
  ruler: (
    <>
      <rect x="3" y="7" width="18" height="10" rx="2" />
      <path d="M7 7v4M11 7v3M15 7v4M19 7v3" />
    </>
  ),
  bath: <path d="M4 12h16v2.5a4.5 4.5 0 0 1-4.5 4.5h-7A4.5 4.5 0 0 1 4 14.5V12zM6 12V6.5A2.5 2.5 0 0 1 8.5 4c1.2 0 2 .7 2.3 1.6M7 19l-1 2M17 19l1 2" />,
  sofa: <path d="M5 11V8.5A2.5 2.5 0 0 1 7.5 6h9A2.5 2.5 0 0 1 19 8.5V11M3.5 12.5A1.5 1.5 0 0 1 6.5 12.5V15h11v-2.5a1.5 1.5 0 0 1 3 0V18h-17v-5.5zM6 18v2M18 18v2" />,
  calendar: (
    <>
      <rect x="4" y="5.5" width="16" height="14" rx="2.5" />
      <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
    </>
  ),
};

export const FactIcon = ({ name }: { name: keyof typeof FACT_PATHS }) => <Icon size={20}>{FACT_PATHS[name]}</Icon>;

export const StarIcon = () => (
  <svg className="hf-ic" width={15} height={15} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M12 3.2l2.6 5.5 6 .7-4.5 4.1 1.2 5.9L12 16.5l-5.3 2.9 1.2-5.9-4.5-4.1 6-.7z" />
  </svg>
);
export const CircleCheckIcon = () => (
  <Icon size={18}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8.5 12.3l2.5 2.5 4.6-5" />
  </Icon>
);
export const ClockIcon = () => (
  <Icon size={18}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </Icon>
);
export const CircleCrossIcon = () => (
  <Icon size={18}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9 9l6 6M15 9l-6 6" />
  </Icon>
);
